import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Check, ChevronLeft, ChevronRight, CircleSlash, Download, FileSpreadsheet, FileText, Mail, MessageSquareText, Microscope, Package, Paperclip, Printer, Rows3,
  Upload, X,
} from 'lucide-react';
import {
  addLaboratoryCustomerFiles, answerLaboratoryCustomerQc, downloadLaboratoryReceivingForm, loadCollectionPrinters, loadLaboratoryCustomerSubmission,
  loadLaboratoryCustomerSubmissions, openLaboratoryCustomerFile, printLaboratoryCustomerLabels, removeLaboratoryCustomerFile, sendLaboratoryQcResults,
} from './api';
import { useXomicsAuth } from './AuthContext';
import { LabPagination, LabResource, labError, labWhen } from './LaboratoryParts';
import { SharedDataCard, SharedDataCell, useSharedData } from './LaboratorySharedData';
import { workflowName } from './LaboratoryText';
import QcTable from './QcTable';
import { Empty, Head, Pill, SummaryBar, SummaryItem, XomicsSelect } from './parts';
import { FILE_ACCEPT, MAX_FILE_BYTES, fileForUpload, fileSize } from './qcResults';
import SampleAnswersTable from './SampleAnswersTable';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-inventory.css';
import './laboratory-work.css';
import './laboratory-customers.css';

// What our customers sent this laboratory (the laboratory, 19 and 22 October 2026): each box,
// whose and for which project; and one box: our first quality check of its samples, a row each with
// the customer's answer (go on, or stop), what the laboratory tells the customer, the files of both
// sides, and the customer's own sample form (their names beside our codes). From here the laboratory
// downloads the box as its Sample Receiving and QC Verification Form, adds its QC reports, tells the
// customer their results are in, and records an answer the customer gave another way.
const text = (key, values) => tx(`ui.lab.customers.${key}`, values);
const counted = (key, count) => text(count === 1 ? `${key}_one` : key, { count });
const PAGE = 50;
const SAMPLE_TONES = { waiting: 'flat', received: 'info', in_progress: 'info', completed: 'ok', stopped: 'bad' };
const boxLabel = (project, number) => text(project.kind === 'continuous' ? 'box_label' : 'project_label', { project: project.number, number });

function BoxRow({ entry, onOpen }) {
  const qc = entry.qc;
  return <li><button type="button" className="xo-labcust-row" onClick={() => onOpen(entry)}>
    <span className="xo-labcust-icon" aria-hidden="true"><Package /></span>
    <span className="xo-labcust-main">
      <strong>{entry.customer.name}</strong>
      <span>{boxLabel(entry.project, entry.number)} · {workflowName(entry.workflow.code)} · {entry.sampleType.name}</span>
      <small>{text('sent_by', { date: labWhen(entry.sentAt), name: entry.sentBy || '' })}</small>
    </span>
    <span className="xo-labcust-states">
      <Pill tone={entry.received === entry.samples ? 'ok' : entry.received > 0 ? 'info' : 'flat'}>{text('received_of', { received: entry.received, count: entry.samples })}</Pill>
      {qc.waiting > 0 && <Pill tone="warn">{counted('qc_waiting', qc.waiting)}</Pill>}
      {qc.checked > 0 && qc.waiting === 0 && <Pill tone="ok">{text('qc_answered', { count: qc.checked })}</Pill>}
      {entry.files.customer + entry.files.laboratory > 0 && <span className="xo-labcust-files"><Paperclip aria-hidden="true" />{entry.files.customer + entry.files.laboratory}</span>}
    </span>
    <ChevronRight className="xo-labcust-open" aria-hidden="true" />
  </button></li>;
}

function SubmissionList({ onOpen }) {
  const [offset, setOffset] = useState(0);
  const load = useCallback(() => loadLaboratoryCustomerSubmissions({ limit: PAGE, offset }), [offset]);
  const resource = useLiveResource(['samples'], load);
  return <LabResource resource={resource}>{data => <>
    <div className="xo-product-results-heading" role="status"><span>{text(data.total === 1 ? 'count_one' : 'count', { count: data.total })}</span></div>
    {data.submissions.length === 0 ? <Empty title={text('empty')} detail={text('empty_hint')} />
      : <ol className="xo-labcust-list">{data.submissions.map(entry => <BoxRow key={`${entry.project.uuid}-${entry.number}`} entry={entry} onOpen={onOpen} />)}</ol>}
    <LabPagination data={data} loading={resource.loading} onOffset={setOffset} />
  </>}</LabResource>;
}

// Our first quality check of the box, a row a sample, under what we told the customer with the
// results (as the customer sees it); telling them starts here. While samples wait for the customer,
// an answer they gave another way (by email, on the phone) is recorded here for them, with how they
// gave it.
function QcSection({ box, mayWork, onDone }) {
  const [chosen, setChosen] = useState({}), [note, setNote] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [telling, setTelling] = useState(false);
  const checked = box.samples.filter(sample => sample.qc !== null);
  if (checked.length === 0) return null;
  const waiting = checked.filter(sample => sample.qc.waiting);
  const answers = waiting.filter(sample => chosen[sample.position]).map(sample => ({ position: sample.position, answer: chosen[sample.position] }));
  const stops = answers.filter(entry => entry.answer === 'stop').length;
  const choose = (position, answer) => setChosen(current => ({ ...current, [position]: current[position] === answer ? undefined : answer }));
  async function save() {
    setBusy(true); setError('');
    try { await answerLaboratoryCustomerQc(box.project.uuid, box.submission.number, { answers, note: note.trim() }); setChosen({}); setNote(''); onDone(); }
    catch (caught) { setError(labError(caught)); } finally { setBusy(false); }
  }
  const sent = box.submission.qcSent;
  return <section className="xo-labcust-card xo-labcust-qc">
    <header className="xo-labcust-qc-head"><span className="xo-labcust-card-icon" aria-hidden="true"><Microscope /></span>
      <div><h3>{text('qc_title')}</h3><p>{waiting.length > 0 ? counted('qc_line_waiting', waiting.length) : text('qc_line_done')}</p></div>
      {mayWork && !telling && <button type="button" className={sent ? 'xo-btn-2' : 'xo-btn'} onClick={() => setTelling(true)}>
        <Mail aria-hidden="true" />{text(sent ? 'tell_again' : 'tell')}</button>}
    </header>
    {telling ? <TellCustomer box={box} onClose={() => setTelling(false)} onDone={onDone} />
      : sent && <p className="xo-labcust-said"><small>{text('told_line', { date: labWhen(sent.at), name: sent.by || '' })}</small>{sent.message}</p>}
    <QcTable samples={box.samples} compare={box.qcCompare} columns={box.columns} audience="lab"
      chosen={chosen} onChoose={mayWork && waiting.length > 0 ? choose : null} busy={busy} />
    {mayWork && waiting.length > 0 && <div className="xo-labcust-bar">
      <span>{answers.length === 0 ? text('answer_line')
        : [answers.length - stops > 0 && counted('answer_go', answers.length - stops), stops > 0 && counted('answer_stop', stops)].filter(Boolean).join(' · ')}</span>
      <input value={note} maxLength={1000} disabled={busy} onChange={event => setNote(event.target.value)} placeholder={text('answer_note_placeholder')}
        aria-label={text('answer_note')} />
      <button type="button" className="xo-btn" disabled={busy || answers.length === 0 || note.trim() === ''} onClick={save}>
        {text(busy ? 'saving' : 'answer_save')}</button>
    </div>}
    {stops > 0 && <p className="xo-labcust-warning" role="status"><CircleSlash aria-hidden="true" />{counted('stop_warning', stops)}</p>}
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </section>;
}

// Telling the customer their QC results are in: everyone at the customer gets an email with a link to
// the box in their portal, and what we write here with it.
function TellCustomer({ box, onClose, onDone }) {
  const [message, setMessage] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  async function send() {
    setBusy(true); setError('');
    try { await sendLaboratoryQcResults(box.project.uuid, box.submission.number, message.trim()); onClose(); onDone(); }
    catch (caught) { setError(labError(caught)); setBusy(false); }
  }
  return <div className="xo-labcust-tell">
    <p>{text('tell_line')}</p>
    <textarea rows={3} maxLength={2000} value={message} disabled={busy} onChange={event => setMessage(event.target.value)}
      placeholder={text('tell_placeholder')} aria-label={text('tell_message')} />
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <div className="xo-labcust-card-actions">
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onClose}>{text('cancel')}</button>
      <button type="button" className="xo-btn" disabled={busy} onClick={send}><Mail aria-hidden="true" />{text(busy ? 'telling' : 'tell_send')}</button>
    </div>
  </div>;
}

// The files of the box: the customer's (index sample sheets and the like) and ours (QC reports,
// which we add and take away).
function Files({ box, mayWork, onDone }) {
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const input = useRef(null);
  const theirs = box.submission.files.filter(file => file.side === 'customer');
  const ours = box.submission.files.filter(file => file.side === 'laboratory');
  async function add(list) {
    setBusy(true); setError('');
    const problems = [];
    for (const file of list) {
      if (file.size > MAX_FILE_BYTES) { problems.push(text('file_too_large', { name: file.name })); continue; }
      try { await addLaboratoryCustomerFiles(box.project.uuid, box.submission.number, [await fileForUpload(file)]); }
      catch (caught) { problems.push(`${file.name}: ${labError(caught)}`); }
    }
    onDone();
    setError(problems.join(' · ')); setBusy(false);
  }
  async function remove(file) {
    setBusy(true); setError('');
    try { await removeLaboratoryCustomerFile(box.project.uuid, box.submission.number, file.uuid); onDone(); } catch (caught) { setError(labError(caught)); }
    finally { setBusy(false); }
  }
  const row = (file, own) => <li key={file.uuid}>
    <button type="button" className="xo-labcust-attachment" onClick={() => openLaboratoryCustomerFile(file.uuid).catch(caught => setError(labError(caught)))}>
      <FileText aria-hidden="true" /><span><strong>{file.name}</strong><small>{fileSize(file.bytes)} · {labWhen(file.addedAt)}{file.addedBy ? ` · ${file.addedBy}` : ''}</small></span>
      <Download aria-hidden="true" /></button>
    {own && mayWork && <button type="button" className="xo-icon-btn" aria-label={text('file_remove', { name: file.name })} disabled={busy} onClick={() => remove(file)}>
      <X aria-hidden="true" /></button>}
  </li>;
  return <section className="xo-labcust-card">
    <header><span className="xo-labcust-card-icon" aria-hidden="true"><Paperclip /></span>
      <div><h3>{text('files_title')}</h3><p>{text('files_line')}</p></div></header>
    <div className="xo-labcust-files-groups">
      <div><small>{text('files_theirs')}</small>
        {theirs.length === 0 ? <p className="xo-labcust-none">{text('files_none_theirs')}</p> : <ul>{theirs.map(file => row(file, false))}</ul>}</div>
      <div><small>{text('files_ours')}</small>
        {ours.length === 0 ? <p className="xo-labcust-none">{text('files_none_ours')}</p> : <ul>{ours.map(file => row(file, true))}</ul>}</div>
    </div>
    {mayWork && <div className="xo-labcust-card-actions">
      <input ref={input} type="file" multiple hidden accept={FILE_ACCEPT} onChange={event => { const list = [...(event.target.files || [])]; event.target.value = ''; add(list); }} />
      <button type="button" className="xo-btn-2" disabled={busy} onClick={() => input.current?.click()}><Upload aria-hidden="true" />{text(busy ? 'files_adding' : 'files_add')}</button>
    </div>}
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </section>;
}

// Our labels for the box's tubes. The customer names each tube; the scan reads our code. So when the
// box arrives each tube gets our label, our code to scan with their name under it, stuck on the tube
// of that name, and is then scanned in as any other. On the laboratory's specimen-label printer; a
// label can be printed again at any time.
function PrintLabels({ box }) {
  const { permissions } = useXomicsAuth();
  const mayPrint = permissions.includes('LAB_SAMPLE_RECEIVE');
  const [printers, setPrinters] = useState(null), [printer, setPrinter] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [printed, setPrinted] = useState(0);
  useEffect(() => {
    if (!mayPrint) return undefined;
    let current = true;
    loadCollectionPrinters().then(list => { if (current) { setPrinters(list); if (list.length === 1) setPrinter(list[0].deviceId); } })
      .catch(() => { if (current) setPrinters([]); });
    return () => { current = false; };
  }, [mayPrint]);
  async function print() {
    if (busy || printer === '') return;
    setBusy(true); setError(''); setPrinted(0);
    try { setPrinted((await printLaboratoryCustomerLabels(box.project.uuid, box.submission.number, printer)).printed); }
    catch (caught) { setError(labError(caught)); }
    finally { setBusy(false); }
  }
  if (!mayPrint || printers === null) return null;
  return <div className="xo-labcust-labels">
    {printers.length === 0 ? <span className="xo-lab-muted">{tx('ui.lab.scan.no_printer')}</span> : <>
      {printers.length > 1 && <XomicsSelect value={printer} onChange={event => setPrinter(event.target.value)} aria-label={tx('ui.lab.scan.label_printer')} disabled={busy}>
        <option value="">{tx('ui.lab.scan.label_printer')}</option>
        {printers.map(item => <option key={item.deviceId} value={item.deviceId}>{item.deviceId} · {item.type.model}</option>)}
      </XomicsSelect>}
      <button type="button" className="xo-btn-2" onClick={print} disabled={busy || printer === ''}><Printer aria-hidden="true" />
        {busy ? text('labels_printing') : counted('labels_print', box.samples.length)}</button>
      {printed > 0 && <span className="xo-scan-mark"><Check aria-hidden="true" />{counted('labels_printed', printed)}</span>}
    </>}
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </div>;
}

// Their samples as they sent them: their names beside our codes, where each is, and what they wrote of
// each (the form's columns in groups, so the table fits the page), with our labels to print for them.
// Open while any of the box is still to arrive or be checked.
function TheirForm({ box, mayWork, shared }) {
  const lead = [
    { key: 'name', label: text('their_name'), cell: sample => <strong>{sample.sampleName}</strong> },
    { key: 'code', label: text('our_code'), cell: sample => <code>{sample.code}</code> },
    { key: 'where', label: text('where'), cell: sample => <Pill tone={SAMPLE_TONES[sample.status] || 'flat'}>{tx(`ui.customer_portal.status.${sample.status}`)}</Pill> },
    { key: 'shared', label: text('data_title'), cell: sample => <SharedDataCell sample={sample} mayWork={mayWork} shared={shared} /> },
  ];
  return <details className="xo-labcust-form" open={box.samples.some(sample => sample.qc === null)}>
    <summary><FileSpreadsheet aria-hidden="true" />{counted('their_form', box.samples.length)}</summary>
    <div className="xo-labcust-form-bar"><p className="xo-lab-muted">{text('labels_hint')}</p><PrintLabels box={box} /></div>
    <SampleAnswersTable columns={box.columns} samples={box.samples} lead={lead} />
  </details>;
}

function SubmissionView({ projectUuid, number, onBack }) {
  const { permissions } = useXomicsAuth();
  const mayWork = permissions.includes('LAB_WORK_ADVANCE');
  const [error, setError] = useState(''), [form, setForm] = useState(false);
  const shared = useSharedData(projectUuid);
  const load = useCallback(() => loadLaboratoryCustomerSubmission(projectUuid, number), [projectUuid, number]);
  const resource = useLiveResource(['samples'], load);
  // After each change the box is read again.
  const done = () => resource.refresh();
  async function download(box) {
    setForm(true); setError('');
    try { await downloadLaboratoryReceivingForm(box.project.uuid, box.submission.number); } catch (caught) { setError(labError(caught)); } finally { setForm(false); }
  }
  return <LabResource resource={resource}>{box => {
    const qc = box.samples.filter(sample => sample.qc !== null);
    const count = (result) => qc.filter(sample => sample.qc.result === result).length;
    return <>
      <div className="xo-head xo-labcust-head">
        <div className="xo-labcust-title">
          <button type="button" className="xo-labcust-back" onClick={onBack} aria-label={text('back')} title={text('back')}><ChevronLeft aria-hidden="true" /></button>
          <div className="xo-head-text">
            <h1>{box.project.customer.name} · {boxLabel(box.project, box.submission.number)}</h1>
            <p>{workflowName(box.project.workflow.code)} · {box.submission.sampleType.name} · {text('sent_by', { date: labWhen(box.submission.sentAt), name: box.submission.sentBy || '' })}</p>
            {box.submission.note && <p className="xo-labcust-note"><MessageSquareText aria-hidden="true" />
              <span><strong>{text('their_note')}</strong> {box.submission.note}</span></p>}
          </div>
        </div>
        <div className="xo-head-actions">
          {box.receivingForm && mayWork && <Link className="xo-btn-2" to="/xomics/laboratory/batch"><Rows3 aria-hidden="true" />{text('form_batch')}</Link>}
          {box.receivingForm && <button type="button" className="xo-btn" disabled={form} onClick={() => download(box)}>
            <FileSpreadsheet aria-hidden="true" />{text(form ? 'form_downloading' : 'form_download')}</button>}
        </div>
      </div>
      {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <SummaryBar>
        <SummaryItem value={`${box.submission.receivedCount}/${box.submission.sentCount}`} label={text('stat_here')} />
        <SummaryItem value={qc.length} label={text('stat_checked')} />
        <SummaryItem value={count('waiting')} label={text('stat_waiting')} tone={count('waiting') > 0 ? 'warn' : 'flat'} />
        <SummaryItem value={count('proceed')} label={text('stat_going_on')} />
        <SummaryItem value={count('stop') + count('request_new_sample')} label={text('stat_stopped')} />
      </SummaryBar>
      {/* While tubes are still to arrive, their samples (and our labels) come first: that is the next step. */}
      {box.submission.receivedCount < box.submission.sentCount && <TheirForm box={box} mayWork={mayWork} shared={shared} />}
      <QcSection box={box} mayWork={mayWork} onDone={done} />
      <Files box={box} mayWork={mayWork} onDone={done} />
      <SharedDataCard box={box} mayWork={mayWork} shared={shared} />
      {box.submission.receivedCount >= box.submission.sentCount && <TheirForm box={box} mayWork={mayWork} shared={shared} />}
    </>;
  }}</LabResource>;
}

export default function XomicsLabCustomers() {
  const [params, setParams] = useSearchParams();
  const project = params.get('project'), number = params.get('submission');
  const open = entry => setParams({ project: entry.project.uuid, submission: String(entry.number) });
  const back = () => setParams({});
  return <div className="xo-products-page xo-lab-work-page xo-labcust-page">
    {project && number ? <SubmissionView key={`${project}-${number}`} projectUuid={project} number={Number(number)} onBack={back} />
      : <><Head title={text('title')} subtitle={text('subtitle')} /><SubmissionList onOpen={open} /></>}
  </div>;
}
